/**
 * MyShop — Catálogo de produtos IHTP
 *
 * Design IHTP UI Kit:
 *   palette: #0F1829 (navy primary), #978D78 (taupe), #6F7559 (sage),
 *            #6F4F1D (umber), #FAF8F2 (cream), #E6E1D6 (border)
 *   fonts:   Fraunces serif (headers), system sans (body)
 *
 * Funcionalidades:
 *   - Hero serif headline + breve subtítulo (estilo editorial)
 *   - Search bar com debounce 300ms (top sticky on scroll)
 *   - Category chips (horizontal scroll em mobile)
 *   - Sort dropdown
 *   - Product grid responsivo (2 mobile / 3 tablet / 4 desktop)
 *   - Cards com imagem placeholder gradient único por categoria
 *   - Stock badge + NetPoints accent
 *   - "Adicionar ao Carrinho" inline (chama myApi.cartAdd se logado,
 *     senão redirect para /login com cartIntent)
 *   - Skeleton loading · infinite scroll · empty state custom
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search, ShoppingCart, Filter, X, ChevronDown,
  ArrowRight, Loader2,
} from "lucide-react";
import { myApi } from "../api";
import { gaEcommerce } from "@/lib/ga";
import { useMyAuth } from "../contexts/MyAuthContext";

const API = "";

// Imagens hero (photography style — referência IHTP brand)
const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1517842645767-c639042777db?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1531538606174-0f90ff5dce83?auto=format&fit=crop&w=2400&q=80",
];

// Cor accent suave por categoria (sem gradients vibrantes)
const CAT_TINTS = {
  MM: "#1a2542", MO: "#4A5E2E", RV: "#6F4F1D", LI: "#0F1829",
  SV: "#3d4a72", CS: "#6F4F1D", IH: "#4A5E2E", CO: "#1a2542",
  CD: "#0F1829", SW: "#4A5E2E", PM: "#6F4F1D", LA: "#0F1829",
  CC: "#3d4a72", VL: "#6F4F1D", CE: "#0F1829", BE: "#4A5E2E",
};
const DEFAULT_TINT = "#0F1829";

const fmt = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);
const title = (p) => p.Title || p.Descricao || "Produto";

function ProductCardSkeleton() {
  return (
    <div className="rounded-[4px] border border-[#E6E1D6] bg-white overflow-hidden animate-pulse">
      <div className="aspect-[4/5] bg-[#F3F0E7]" />
      <div className="p-5 space-y-2">
        <div className="h-2.5 bg-[#F3F0E7] rounded w-20" />
        <div className="h-4 bg-[#F3F0E7] rounded w-full" />
        <div className="h-4 bg-[#F3F0E7] rounded w-3/4" />
        <div className="h-5 bg-[#F3F0E7] rounded w-24 mt-3" />
      </div>
    </div>
  );
}

function ProductCard({ product, onAdd, adding }) {
  const tint = CAT_TINTS[product.ID_TipoProduto] || DEFAULT_TINT;
  const inStock = (product.Stock ?? 0) > 0;
  const productTitle = product.Title || product.Descricao || "";

  return (
    <div className="group rounded-[4px] border border-[#E6E1D6] bg-white overflow-hidden hover:border-[#0F1829] transition-all duration-300"
      data-testid={`product-card-${product.ID_Produto}`}>
      <Link to={`/my/loja/${product.Friendly_url || product.ID_Produto}`} className="block">
        <div className="aspect-[4/5] relative overflow-hidden" style={{ backgroundColor: tint }}>
          {/* Photography placeholder pattern — subtle, no glyphs */}
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4) 0%, transparent 60%)" }} />
          {!inStock && (
            <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] text-white bg-red-700 px-2.5 py-1">
              Esgotado
            </span>
          )}
        </div>
        <div className="p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#978D78] mb-2 font-medium">
            {product.Categoria || product.ID_TipoProduto}
          </div>
          <h3 className="text-[15px] font-semibold text-[#0F1829] leading-snug line-clamp-2 min-h-[42px] mb-4">
            {productTitle}
          </h3>
          <div className="flex items-baseline justify-between gap-2 pt-3 border-t border-[#F3F0E7]">
            <div className="font-bold text-[#0F1829] tabular-nums text-[18px]">
              {fmt(product.Preco)}
            </div>
            {Number(product.NetPoints || 0) > 0 && (
              <span className="text-[10px] text-[#978D78] uppercase tracking-[0.15em]">
                {Number(product.NetPoints).toFixed(0)} NP
              </span>
            )}
          </div>
        </div>
      </Link>
      {/* Botão estilo editorial uppercase com arrow */}
      <button
        onClick={() => onAdd(product)}
        disabled={!inStock || adding}
        data-testid={`product-add-${product.ID_Produto}`}
        className="w-full py-3 px-5 bg-[#0F1829] text-white text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-black transition-colors duration-200 disabled:bg-[#978D78] disabled:cursor-not-allowed flex items-center justify-center gap-2 group/btn"
      >
        {adding ? (
          <><Loader2 size={13} className="animate-spin" /> A adicionar</>
        ) : (
          <>Adicionar ao carrinho <ArrowRight size={13} className="group-hover/btn:translate-x-0.5 transition-transform" /></>
        )}
      </button>
    </div>
  );
}

const SORTS = [
  { value: "featured", label: "Em destaque" },
  { value: "newest", label: "Novidades" },
  { value: "price_asc", label: "Preço: menor primeiro" },
  { value: "price_desc", label: "Preço: maior primeiro" },
  { value: "name_asc", label: "Nome A→Z" },
];

export default function MyShop() {
  const { user } = useMyAuth();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [adding, setAdding] = useState(null);
  const [toast, setToast] = useState(null);
  const [queryDraft, setQueryDraft] = useState(params.get("q") || "");
  const sentinelRef = useRef(null);

  const filters = useMemo(() => ({
    q: params.get("q") || "",
    category: params.get("category") || "",
    sort: params.get("sort") || "featured",
    page: 1,
  }), [params]);

  // Carregar categorias 1x
  useEffect(() => {
    fetch(`${API}/api/public/products/categories`)
      .then(r => r.json())
      .then(d => setCategories(d.items || []))
      .catch(() => setCategories([]));
  }, []);

  // Carregar página 1 quando filtros mudam
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) setLoading(true);
      const p = new URLSearchParams({
        q: filters.q, category: filters.category, sort: filters.sort,
        page: 1, page_size: 24,
      });
      try {
        const d = await fetch(`${API}/api/public/products/catalog?${p}`).then(r => r.json());
        if (cancelled) return;
        setProducts(d.items || []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filters.q, filters.category, filters.sort]);

  // Debounce de pesquisa (300ms)
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (queryDraft) next.set("q", queryDraft); else next.delete("q");
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [queryDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const loadMore = useCallback(async () => {
    const currentPage = Math.ceil(products.length / 24);
    if (loadingMore || currentPage >= totalPages) return;
    setLoadingMore(true);
    const p = new URLSearchParams({
      q: filters.q, category: filters.category, sort: filters.sort,
      page: currentPage + 1, page_size: 24,
    });
    try {
      const r = await fetch(`${API}/api/public/products/catalog?${p}`).then(r => r.json());
      setProducts(prev => [...prev, ...(r.items || [])]);
    } finally {
      setLoadingMore(false);
    }
  }, [products.length, totalPages, loadingMore, filters]);

  // Infinite scroll IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "400px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [loadMore]);

  const handleAdd = async (product) => {
    const gaItems = [{ item_id: String(product.ID_Produto), item_name: title(product), item_brand: "IHTP", price: Number(product.Preco) || 0, quantity: 1 }];
    if (!user) {
      // Cart intent flow para não-logados
      gaEcommerce("add_to_cart", gaItems, Number(product.Preco) || 0);
      try {
        const existing = JSON.parse(sessionStorage.getItem("ihtp.pending_cart") || "{}");
        const items = existing.items || [];
        const idx = items.findIndex(i => i.ID_Produto === product.ID_Produto);
        if (idx >= 0) items[idx].Quantidade = (items[idx].Quantidade || 1) + 1;
        else items.push({ ID_Produto: product.ID_Produto, Quantidade: 1 });
        sessionStorage.setItem("ihtp.pending_cart", JSON.stringify({ items }));
        window.location.href = `/login?next=${encodeURIComponent("/my/carrinho")}`;
      } catch { window.location.href = "/login"; }
      return;
    }
    setAdding(product.ID_Produto);
    try {
      await myApi.cartAdd(product.ID_Produto, 1);
      gaEcommerce("add_to_cart", gaItems, Number(product.Preco) || 0);
      setToast({ type: "success", title: title(product), msg: "Adicionado ao carrinho" });
      window.dispatchEvent(new Event("cart-updated"));
    } catch (e) {
      setToast({ type: "error", title: "Erro", msg: e.message || "Falha a adicionar" });
    } finally {
      setAdding(null);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const activeFilters = (filters.q ? 1 : 0) + (filters.category ? 1 : 0);

  return (
    <div data-testid="my-shop-page" className="space-y-10">
      {/* Hero photography editorial — full-bleed dark image with overlay */}
      <div className="relative overflow-hidden rounded-[4px] -mx-4 lg:-mx-8 lg:mt-0">
        <div className="absolute inset-0 bg-[#0F1829]">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-40 transition-opacity duration-1000"
            style={{ backgroundImage: `url('${HERO_IMAGES[0]}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1829]/60 via-[#0F1829]/40 to-[#0F1829]/90" />
        </div>
        <div className="relative px-6 lg:px-16 py-20 lg:py-28 text-white">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.35em] text-white/70 mb-5 font-medium">
              Loja IHTP
            </div>
            <h1 className="font-semibold leading-[1.1] mb-6 text-white"
              style={{ fontSize: "clamp(40px, 6vw, 64px)" }}>
              Liberta o teu <span className="italic font-normal">potencial.</span>
            </h1>
            <p className="text-white/85 text-base lg:text-lg max-w-xl leading-relaxed mb-8">
              Cursos, livros, áudios motivacionais, coaching e ferramentas
              para transformar a forma como vives, trabalhas e lideras.
            </p>
            {/* Editorial counter stats */}
            <div className="grid grid-cols-3 gap-8 lg:gap-12 max-w-xl pt-6 border-t border-white/15">
              <div>
                <div className="text-3xl lg:text-4xl font-bold tabular-nums">{total || "—"}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-1">Produtos</div>
              </div>
              <div>
                <div className="text-3xl lg:text-4xl font-bold tabular-nums">{categories.length}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-1">Categorias</div>
              </div>
              <div>
                <div className="text-3xl lg:text-4xl font-bold tabular-nums">+22</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-1">Anos</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search + Sort */}
      <div className="sticky top-0 z-20 bg-[#FAF8F2]/95 backdrop-blur-md -mx-4 lg:-mx-8 px-4 lg:px-8 py-4 border-b border-[#E6E1D6]">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center max-w-7xl">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#978D78]" />
            <input
              data-testid="shop-search-input"
              type="text"
              value={queryDraft}
              onChange={(e) => setQueryDraft(e.target.value)}
              placeholder="Procurar por nome, referência ou descrição..."
              className="w-full pl-11 pr-10 py-3 rounded-[4px] bg-white border border-[#E6E1D6] text-sm text-[#0F1829] placeholder:text-[#978D78] focus:outline-none focus:border-[#0F1829] transition"
            />
            {queryDraft && (
              <button onClick={() => setQueryDraft("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#978D78] hover:text-[#0F1829]" aria-label="Limpar">
                <X size={15} />
              </button>
            )}
          </div>
          <div className="relative">
            <select
              data-testid="shop-sort-select"
              value={filters.sort}
              onChange={(e) => setFilter("sort", e.target.value)}
              className="appearance-none pl-4 pr-10 py-3 rounded-[4px] bg-white border border-[#E6E1D6] text-xs uppercase tracking-[0.15em] text-[#0F1829] focus:outline-none focus:border-[#0F1829] cursor-pointer font-medium"
            >
              {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#978D78] pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Category chips — rectangular editorial */}
      <div className="-mx-4 lg:mx-0 px-4 lg:px-0 overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-2 min-w-max pb-1">
          <button
            data-testid="cat-chip-all"
            onClick={() => setFilter("category", "")}
            className={`shrink-0 text-[11px] uppercase tracking-[0.18em] font-semibold px-4 py-2 rounded-[4px] border transition whitespace-nowrap ${
              !filters.category
                ? "bg-[#0F1829] text-white border-[#0F1829]"
                : "bg-white text-[#4B5563] border-[#E6E1D6] hover:border-[#0F1829]"
            }`}
          >
            Todos {!filters.category && total > 0 && <span className="ml-1.5 opacity-70 normal-case tracking-normal">({total})</span>}
          </button>
          {categories.map(c => (
            <button
              key={c.ID_TipoProduto}
              data-testid={`cat-chip-${c.ID_TipoProduto}`}
              onClick={() => setFilter("category", c.ID_TipoProduto)}
              className={`shrink-0 text-[11px] uppercase tracking-[0.18em] font-semibold px-4 py-2 rounded-[4px] border transition whitespace-nowrap ${
                filters.category === c.ID_TipoProduto
                  ? "bg-[#0F1829] text-white border-[#0F1829]"
                  : "bg-white text-[#4B5563] border-[#E6E1D6] hover:border-[#0F1829]"
              }`}
            >
              {c.Descricao} <span className="ml-1 opacity-60 text-[10px] normal-case tracking-normal">({c.n})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Active filters bar */}
      {activeFilters > 0 && (
        <div className="flex items-center gap-2 text-xs text-[#4B5563]">
          <Filter size={12} className="text-[#978D78]" />
          <span>A mostrar <strong className="text-[#0F1829]">{products.length}</strong> de <strong className="text-[#0F1829]">{total}</strong> produtos</span>
          <button onClick={() => { setParams({}); setQueryDraft(""); }}
            className="ml-2 text-[#0F1829] text-[11px] uppercase tracking-[0.18em] font-semibold hover:text-black inline-flex items-center gap-1" data-testid="clear-filters">
            Limpar filtros <ArrowRight size={11} />
          </button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={i} />)}
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-[4px] border border-dashed border-[#E6E1D6] bg-white py-20 text-center" data-testid="shop-empty-state">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#FAF8F2] grid place-items-center mb-4">
            <Search size={20} className="text-[#978D78]" />
          </div>
          <h3 className="font-semibold text-[#0F1829] mb-1 text-xl">Nenhum produto encontrado</h3>
          <p className="text-sm text-[#978D78]">Tenta um termo diferente ou limpa os filtros.</p>
          <button onClick={() => { setParams({}); setQueryDraft(""); }}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-[4px] bg-[#0F1829] text-white text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-black transition">
            Ver todos os produtos <ArrowRight size={13} />
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map(p => (
              <ProductCard key={p.ID_Produto} product={p} onAdd={handleAdd} adding={adding === p.ID_Produto} />
            ))}
          </div>
          <div ref={sentinelRef} />
          {loadingMore && (
            <div className="text-center py-6 text-xs text-[#978D78] uppercase tracking-[0.2em] flex items-center justify-center gap-2">
              <Loader2 size={13} className="animate-spin" /> A carregar mais produtos
            </div>
          )}
          {!loadingMore && products.length >= total && total > 24 && (
            <div className="text-center py-8 text-[10px] text-[#978D78] uppercase tracking-[0.3em]">
              Fim do catálogo — {total} produtos
            </div>
          )}
        </>
      )}

      {/* Toast */}
      {toast && (
        <div
          data-testid="shop-toast"
          className={`fixed bottom-6 right-6 z-50 max-w-sm p-4 rounded-[4px] shadow-2xl border ${
            toast.type === "error" ? "bg-red-50 border-red-200 text-red-800" : "bg-white border-[#0F1829]"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 grid place-items-center rounded-[4px] shrink-0"
              style={{ background: toast.type === "error" ? "#fee2e2" : "#0F1829" }}>
              <ShoppingCart size={15} className={toast.type === "error" ? "text-red-700" : "text-white"} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#0F1829] truncate">{toast.title}</div>
              <div className="text-xs text-[#4B5563] mt-0.5">{toast.msg}</div>
              {toast.type !== "error" && (
                <Link to="/my/carrinho" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0F1829] mt-2.5 hover:text-black">
                  Ver carrinho <ArrowRight size={11} />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
